import React, { useEffect, useRef, useState, useMemo } from 'react';
import Globe, { GlobeMethods } from 'react-globe.gl'; // Import GlobeMethods for ref typing
import * as satellite from 'satellite.js';
import * as THREE from 'three';

// Constants
const EARTH_RADIUS_KM = 6371; // km
const SAT_SIZE = 40; // km (made smaller for label clarity)
const TIME_STEP = 3000; // ms per frame
const ORBIT_PROPAGATION_DURATION_MINUTES = 30; 
const ORBIT_PROPAGATION_STEP_MINUTES = 1; // Step size for orbit calculation
const LABEL_FONT_SIZE = 12; // Adjusted font size for labels
const LABEL_COLOR = 'rgba(255, 255, 255, 0.95)'; // White label text
const LABEL_BG_COLOR = 'rgba(0, 0, 0, 0.5)'; // Semi-transparent black background for label

// Helper Function to convert radians to degrees
const radiansToDegrees = (radians: number): number => (radians * 180) / Math.PI;

// --- Satellite Data Structure ---
interface SatelliteDefinition {
  name: string;
  tle1: string;
  tle2: string;
  color: string; // Color for the satellite object and its orbit
}

// --- TLE Data with Colors ---
// Using the TLEs provided by the user
const SATELLITE_DEFINITIONS: SatelliteDefinition[] = [
  { name: 'SENTINEL-1A', tle1: '1 39634U 14016A   25115.15050986  .00000378  00000+0  89920-4 0  9994', tle2: '2 39634  98.1812 123.7417 0001388  88.4905 271.6454 14.59199451589082', color: '#FF6347' }, // Tomato Red
  { name: 'SENTINEL-1B', tle1: '1 41456U 16025A   25115.19138615  .00007546  00000+0  67332-3 0  9997', tle2: '2 41456  98.1485 154.2807 0018566  81.4304 278.9018 14.96140755481213', color: '#A9A9A9' }, // Dark Gray (Note: Likely inactive)
  { name: 'SENTINEL-1C', tle1: '1 62261U 24235A   25115.25250108  .00000472  00000+0  10982-3 0  9995', tle2: '2 62261  98.1831 123.5350 0001384  88.9555 271.1803 14.59198185 20469', color: '#FF4500' }, // OrangeRed
  { name: 'SENTINEL-2A', tle1: '1 40697U 15028A   25115.17271347  .00000267  00000+0  11841-3 0  9990', tle2: '2 40697  98.5705 191.0013 0001082  98.3279 261.8027 14.30816116513956', color: '#32CD32' }, // LimeGreen
  { name: 'SENTINEL-2B', tle1: '1 42063U 17013A   25114.67604732  .00000282  00000+0  12402-3 0  9998', tle2: '2 42063  98.5648 190.4650 0001304  95.2478 264.8854 14.30825029424792', color: '#228B22' }, // ForestGreen
  { name: 'SENTINEL-2C', tle1: '1 60989U 24157A   25115.20077621  .00000261  00000+0  11604-3 0  9993', tle2: '2 60989  98.5634 191.0663 0001184  97.1280 263.0037 14.30816692 33216', color: '#ADFF2F' }, // GreenYellow
  { name: 'SENTINEL-3A', tle1: '1 41335U 16011A   25115.17228249  .00000257  00000+0  12436-3 0  9996', tle2: '2 41335  98.6279 183.5131 0000954  95.1995 264.9294 14.26729422478450', color: '#1E90FF' }, // DodgerBlue
  { name: 'SENTINEL-3B', tle1: '1 43437U 18039A   25114.58426231  .00000280  00000+0  13386-3 0  9999', tle2: '2 43437  98.6325 183.0323 0001604  86.5914 273.5449 14.26737148364437', color: '#4682B4' }, // SteelBlue
  { name: 'SENTINEL-5P', tle1: '1 42969U 17064A   25114.79897937  .00000177  00000+0  10473-3 0  9992', tle2: '2 42969  98.7554  55.7999 0001120  77.3959 282.7342 14.19513631390204', color: '#FFD700' }, // Gold
  { name: 'SENTINEL-6',  tle1: '1 46984U 20086A   25114.91079060 -.00000046  00000+0  66920-4 0  9999', tle2: '2 46984  66.0405 283.8179 0007874 271.2445  88.7668 12.80929870206875', color: '#DA70D6' }, // Orchid
];

// Interface for processed satellite data including satrec and color
interface SatelliteInfo {
  satrec: satellite.SatRec;
  name: string;
  color: string;
  lat?: number;
  lng?: number;
  alt?: number;
}

// Interface for arc data used by react-globe.gl
interface ArcData {
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  color: string | string[]; // Keep flexibility, but we'll use single color from sat
  name: string;
}

// --- Helper Function to Create Text Sprite ---
const createTextSprite = (text: string, fontSize: number = 16, fontFace: string = 'Arial', textColor: string = 'white', backgroundColor: string = 'rgba(0,0,0,0.2)') => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null; // Still return null if context fails, handle below

    context.font = `${fontSize}px ${fontFace}`;
    const metrics = context.measureText(text);
    const textWidth = metrics.width;
    const canvasWidth = textWidth + fontSize * 0.4;
    const canvasHeight = fontSize * 1.4;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    if (backgroundColor) {
        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    context.font = `${fontSize}px ${fontFace}`;
    context.fillStyle = textColor;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvasWidth / 2, canvasHeight / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
    });

    const sprite = new THREE.Sprite(material);
    const spriteScaleFactor = fontSize /3;
    sprite.scale.set(spriteScaleFactor * (canvasWidth / canvasHeight), spriteScaleFactor, 1);

    return sprite;
};


const MyGlobe: React.FC = () => {
  // Type the ref using GlobeMethods for better type safety
  const globeEl = useRef<GlobeMethods | undefined>();
  const [satelliteData, setSatelliteData] = useState<SatelliteInfo[]>([]);
  const [globeRadius, setGlobeRadius] = useState<number | null>(null);
  const [time, setTime] = useState(new Date());
  const [arcsData, setArcsData] = useState<ArcData[]>([]);

  // Effect for time progression
  useEffect(() => {
    const intervalID = setInterval(() => {
      setTime(currentTime => new Date(+currentTime + TIME_STEP));
    }, TIME_STEP);
    return () => clearInterval(intervalID);
  }, []);

  // Effect to load satellite TLEs from the new structure
  useEffect(() => {
    const satData = SATELLITE_DEFINITIONS.map(({ name, tle1, tle2, color }) => {
      try {
        const satrec = satellite.twoline2satrec(tle1, tle2);
        const prop = satellite.propagate(satrec, new Date());
        if (prop.position && typeof prop.position !== 'boolean') {
          return { satrec, name, color };
        } else {
          console.warn(`Could not initially propagate ${name}. Excluding.`);
          return null;
        }
      } catch (error) {
        console.error(`Error parsing TLE for satellite ${name}:`, error);
        return null;
      }
    }).filter((d): d is SatelliteInfo => d !== null);

    setSatelliteData(satData);
  }, []); // Runs once on mount

  // Calculate current satellite positions
  const currentObjectsData = useMemo(() => {
    if (!satelliteData.length) return [];

    const gmst = satellite.gstime(time);
    return satelliteData.map(d => {
      try {
        const eci = satellite.propagate(d.satrec, time);
        if (eci.position && typeof eci.position !== 'boolean' && eci.velocity && typeof eci.velocity !== 'boolean') {
          const gdPos = satellite.eciToGeodetic(eci.position, gmst);
          const lat = radiansToDegrees(gdPos.latitude);
          const lng = radiansToDegrees(gdPos.longitude);
          const alt = gdPos.height / EARTH_RADIUS_KM;
          return { ...d, lat, lng, alt };
        }
      } catch (error) {
        // console.warn(`Could not propagate ${d.name} at ${time}:`, error);
      }
      return { ...d, lat: undefined, lng: undefined, alt: undefined };
    }).filter(d => d.lat !== undefined && d.lng !== undefined && d.alt !== undefined);
  }, [satelliteData, time]);


  // Calculate orbital trajectories (arcs)
  useEffect(() => {
    if (!satelliteData.length) return;

    const newArcsData: ArcData[] = [];

    satelliteData.forEach((satInfo) => {
      const { satrec, name, color } = satInfo;
      const trajectoryPoints = [];
      const startTime = new Date();

      for (let i = 0; i <= ORBIT_PROPAGATION_DURATION_MINUTES; i += ORBIT_PROPAGATION_STEP_MINUTES) {
        const currentTime = new Date(startTime.getTime() + i * 60000);
        try {
            const gmst = satellite.gstime(currentTime);
            const eci = satellite.propagate(satrec, currentTime);

            if (eci.position && typeof eci.position !== 'boolean') {
                const gdPos = satellite.eciToGeodetic(eci.position, gmst);
                trajectoryPoints.push({
                    lat: radiansToDegrees(gdPos.latitude),
                    lng: radiansToDegrees(gdPos.longitude),
                    alt: gdPos.height / EARTH_RADIUS_KM,
                    name: name
                });
            }
        } catch (error) {
            // console.warn(`Error propagating orbit point for ${name} at step ${i}:`, error);
        }
      }

      for (let i = 0; i < trajectoryPoints.length - 1; i++) {
        const p1 = trajectoryPoints[i];
        const p2 = trajectoryPoints[i + 1];
        if (Math.abs(p1.lat - p2.lat) < 90 && Math.abs(p1.lng - p2.lng) < 180) {
             newArcsData.push({
               startLat: p1.lat,
               startLng: p1.lng,
               endLat: p2.lat,
               endLng: p2.lng,
               color: color,
               name: name
             });
        }
      }
    });

    setArcsData(newArcsData);
  }, [satelliteData]);


  // Memoize satellite base geometry (shared)
  const satGeometry = useMemo(() => new THREE.SphereGeometry(1, 8, 8), []);

  // Custom object rendering function
  const objectThreeObject = useMemo(() => {
    // This outer function is memoized based on globeRadius and satGeometry
    return (d: object): THREE.Object3D => { // Ensure return type is always THREE.Object3D
      // Return an empty group if globeRadius is not set yet, satisfying TS
      if (!globeRadius) return new THREE.Group();

      // Type assertion: We expect 'd' to conform to SatelliteInfo based on objectsData
      const satInfo = d as SatelliteInfo;
      const { name, color } = satInfo;

      // Create satellite mesh
      const material = new THREE.MeshLambertMaterial({ color: color || 'white' });
      const mesh = new THREE.Mesh(satGeometry, material);

      // Scale the mesh
      const satMeshSize = SAT_SIZE * globeRadius / EARTH_RADIUS_KM;
      mesh.scale.set(satMeshSize, satMeshSize, satMeshSize);

      // Create text sprite label
      const sprite = createTextSprite(name, LABEL_FONT_SIZE, 'Arial', LABEL_COLOR, LABEL_BG_COLOR);

      // Create a group to hold mesh and potentially the sprite
      const group = new THREE.Group();
      group.add(mesh);

      // Add sprite only if it was created successfully
      if (sprite) {
          const labelOffset = satMeshSize * 1.5;
          sprite.position.set(0, labelOffset, 0);
          group.add(sprite);
      }

      return group; // Always return a THREE.Group
    };
  }, [globeRadius, satGeometry]); // Dependencies for the memoized function


  // Effect to initialize globe view and radius
  useEffect(() => {
    // Use optional chaining for safer access
    const currentRadius = globeEl.current?.getGlobeRadius();
    if (currentRadius) {
        setGlobeRadius(currentRadius);
        globeEl.current?.pointOfView({ altitude: 3.5 });
    } else if (globeEl.current) { // Check if ref is set before scheduling retry
        // Retry mechanism if radius isn't available immediately
        const timeoutId = setTimeout(() => {
            const retryRadius = globeEl.current?.getGlobeRadius();
            if (retryRadius) {
                 setGlobeRadius(retryRadius);
                 globeEl.current?.pointOfView({ altitude: 3.5 });
            } else {
                console.error("Could not get globe radius after retry.");
            }
        }, 500); // Wait 500ms
        // Cleanup timeout on component unmount or if radius is found sooner
        return () => clearTimeout(timeoutId);
    }
  }, []); // Run once on mount


  return (
    <div style={{ height: '100%' }}>
      <Globe
        ref={globeEl}
        backgroundColor="#000000"
        globeImageUrl="/assets/earth-blue-marble-tim2.jpg"
        bumpImageUrl="/assets/earth-topology.png"
        backgroundImageUrl="/assets/night-sky-upscaled.png"
        atmosphereColor="lightskyblue"
        atmosphereAltitude={0.25}
        showAtmosphere={true}

        // Satellite Objects
        objectsData={currentObjectsData}
        objectLat="lat"
        objectLng="lng"
        objectAltitude="alt"
        objectFacesSurfaces={false}
        objectThreeObject={objectThreeObject}

        // Orbital Arcs
        arcsData={arcsData}
        arcStartLat="startLat"
        arcStartLng="startLng"
        arcEndLat="endLat"
        arcEndLng="endLng"
        arcColor="color"
        arcAltitude={0.0}
        arcAltitudeAutoScale={0.0}
        arcStroke={0.2}
        arcDashLength={0.9}
        arcDashGap={0.0}
        arcDashAnimateTime={0}
      />

      {/* Description card */}
      <div style={{
        position: 'absolute',
        top: '90px',
        left: '20px',
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        padding: '10px 15px',
        borderRadius: '8px',
        maxWidth: '300px',
        fontSize: '13px',
        lineHeight: '1.4',
        zIndex: 1
      }}>
        <strong>ESA Sentinel Fleet Tracker</strong><br />
        This globe visualizes the real-time positions and predicted orbital tracks of the European Space Agency’s Sentinel satellites using live TLE data.
      </div>

      {/* Display current simulation time */}
      <div style={{
        position: 'absolute',
        bottom: '40px',
        left: '20px',
        color: 'white',
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: '5px 10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 1 // Ensure it's above the globe canvas
      }}>
        {time.toISOString()}
      </div>
    </div>
  );
};

export default MyGlobe;